import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AppButton } from "components/shared/AppButton";
import { AppPageHeader } from "components/shared/AppPageHeader";
import { ConfirmDialog } from "components/shared/ConfirmDialog";
import { NipperStatus, useNipperSerial } from "hooks/useNipperSerial";
import {
  EcoSaltUnitState,
  useAuthorizeUpgrade,
  useCompleteUpgrade,
  useRecordNipperRead,
} from "hooks/useEcoSaltUpgrade";
import { NipperReading } from "utils/nipper/nipperSession";
import { notify } from "utils/notify";
import EcoSaltAdminVariant from "pages/ecosaltUpgrade/EcoSaltAdminVariant";
import EcoSaltSerialLog from "pages/ecosaltUpgrade/EcoSaltSerialLog";
import EcoSaltViewTabs from "pages/ecosaltUpgrade/EcoSaltViewTabs";
import {
  Bench,
  CardTitle,
  ConnectBand,
  FactLine,
  FieldLabel,
  HeroSize,
  RuntimeBand,
  RuntimeFigure,
  RuntimeHead,
  SerialLine,
  UnitStage,
  Notice,
  PageContainer,
  Row,
  StatusText,
} from "styles/ecosaltUpgrade.styles";

const STATUS_COPY: Record<NipperStatus, { text: string; tone: "ok" | "busy" | "idle" | "error" }> = {
  unsupported: { text: "Web Serial is not available in this browser", tone: "error" },
  idle: { text: "Not connected", tone: "idle" },
  connecting: { text: "Opening serial port...", tone: "busy" },
  connected: { text: "Connected, logging in to the Nipper (Factory)...", tone: "busy" },
  loggedIn: { text: "Connected to the Nipper", tone: "ok" },
};

const COMPLETE_RETRIES = 3;

const apiMessage = (error: unknown, fallback: string) =>
  (error as any)?.response?.data?.message || (error instanceof Error ? error.message : fallback);

const value = (v: unknown, suffix = "") =>
  v === null || v === undefined || v === "" ? "—" : `${v}${suffix}`;

function disabledReason(state: EcoSaltUnitState | null, reading: NipperReading) {
  if (!state) return null;
  const { status } = state.evaluation;
  if (status === "alreadyUpgraded") {
    const from = state.unit.originalCellSize;
    const to = state.unit.upgradedCellSize ?? reading.variant.cellSize;
    return `This unit has already been upgraded (${from}g → ${to}g). No further upgrade is allowed.`;
  }
  if (status === "notEligible") {
    return `This unit's original cell size (${state.unit.originalCellSize}g) has no upgrade available.`;
  }
  return null;
}

const EcoSaltUpgrade: React.FC = () => {
  const queryClient = useQueryClient();
  const nipper = useNipperSerial();
  const { status, reading } = nipper;
  const recordRead = useRecordNipperRead();
  const authorize = useAuthorizeUpgrade();
  const complete = useCompleteUpgrade();

  const [unitState, setUnitState] = useState<EcoSaltUnitState | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState<{ tone: "success" | "warning" | "error"; text: string } | null>(
    null,
  );
  /** While upgrading, the page records the new state itself (via complete). */
  const upgradingRef = useRef(false);
  const recordedRef = useRef<NipperReading | null>(null);
  const readingRef = useRef(reading);
  readingRef.current = reading;

  const record = useCallback(
    async (r: NipperReading) => {
      if (!r.serial) return;
      try {
        const state = await recordRead.mutateAsync({ serial: r.serial, reading: r });
        setUnitState(state);
        setRecordError(null);
        if (state.reconciled) {
          setUpgradeMessage({ tone: "success", text: "A previous upgrade of this unit has now been recorded." });
        }
      } catch (e) {
        setRecordError(apiMessage(e, "Could not record this unit."));
      }
    },
    // mutateAsync is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Record every new reading (connection, reconnection) except the ones the upgrade flow handles.
  useEffect(() => {
    if (!reading) {
      setUnitState(null);
      recordedRef.current = null;
      return;
    }
    if (upgradingRef.current || recordedRef.current === reading) return;
    recordedRef.current = reading;
    void record(reading);
  }, [reading, record]);

  /** Drop the cached "already upgraded" state and record the live Nipper as a new original. */
  const forgetRecord = useCallback(
    (serial: string) => {
      const current = readingRef.current;
      if (!current || current.serial !== serial) return;
      setUnitState(null);
      setUpgradeMessage(null);
      recordedRef.current = null;
      void record(current);
    },
    [record],
  );

  const target = unitState?.evaluation.status === "eligible" ? unitState.evaluation.target : undefined;

  const runUpgrade = async () => {
    if (!reading?.serial || !target) return;
    const serial = reading.serial;
    setUpgrading(true);
    upgradingRef.current = true;
    setUpgradeMessage(null);
    try {
      await authorize.mutateAsync({ serial, currentCellSize: reading.variant.cellSize });
    } catch (e) {
      setUpgradeMessage({ tone: "error", text: apiMessage(e, "The upgrade was not authorised.") });
      setUpgrading(false);
      upgradingRef.current = false;
      setConfirmOpen(false);
      return;
    }

    let after: NipperReading;
    try {
      after = await nipper.writeCellSize(target);
      recordedRef.current = after;
    } catch (e) {
      setUpgradeMessage({
        tone: "error",
        text: `The Nipper was not upgraded: ${e instanceof Error ? e.message : String(e)}`,
      });
      setUpgrading(false);
      upgradingRef.current = false;
      setConfirmOpen(false);
      return;
    }

    let recorded = false;
    for (let attempt = 1; attempt <= COMPLETE_RETRIES && !recorded; attempt++) {
      try {
        await complete.mutateAsync({ serial, reading: after });
        recorded = true;
      } catch (e) {
        if ((e as any)?.response?.status === 409) recorded = true; // already recorded
        else if (attempt < COMPLETE_RETRIES) await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }

    if (recorded) {
      notify.success(`Upgraded to ${target}g`);
      setUpgradeMessage({ tone: "success", text: `Upgrade complete: the Nipper now reports ${after.variant.cellSize}g.` });
      await record(after); // post-upgrade READ, refreshes the "already upgraded" state
      void queryClient.invalidateQueries({ queryKey: ["ecoSaltUpgrades"] });
    } else {
      setUpgradeMessage({
        tone: "warning",
        text:
          "The Nipper was upgraded, but the upgrade could not be saved. " +
          "It will be recorded automatically the next time this unit is connected.",
      });
      setUnitState((s) => (s ? { ...s, evaluation: { status: "alreadyUpgraded" } } : s));
    }
    setUpgrading(false);
    upgradingRef.current = false;
    setConfirmOpen(false);
  };

  const statusCopy = STATUS_COPY[status];
  const connected = status === "connected" || status === "loggedIn" || status === "connecting";
  const reason = reading ? disabledReason(unitState, reading) : null;

  const originalSize = unitState
    ? `${unitState.unit.originalCellSize}g`
    : recordRead.isPending
      ? "Checking..."
      : "—";

  return (
    <PageContainer>
      <AppPageHeader title="EcoSalt Upgrade" />

      <EcoSaltViewTabs
        onDeleted={forgetRecord}
        update={
          <>
      <Bench>
        <ConnectBand aria-labelledby="ecosalt-connection-title">
          <div>
            <CardTitle id="ecosalt-connection-title">Connection</CardTitle>
            <StatusText $tone={statusCopy.tone} role="status" aria-live="polite">
              <i
                className={`pi ${status === "loggedIn" ? "pi-check-circle" : connected ? "pi-spin pi-spinner" : "pi-circle"}`}
                aria-hidden="true"
              />
              {statusCopy.text}
            </StatusText>
          </div>
          {status === "unsupported" ? (
            <Notice $tone="warning">This page needs Web Serial. Use Chrome or Edge on a desktop.</Notice>
          ) : (
            <Row>
              {connected ? (
                <AppButton variant="ghost" onClick={() => void nipper.disconnect()} disabled={upgrading}>
                  Disconnect
                </AppButton>
              ) : (
                <AppButton onClick={() => void nipper.connect()}>Connect</AppButton>
              )}
              {status === "loggedIn" && (
                <AppButton
                  variant="ghost"
                  onClick={() => void nipper.reboot().catch((e) => notify.error(e instanceof Error ? e.message : String(e)))}
                  disabled={upgrading}
                >
                  Reboot Nipper
                </AppButton>
              )}
              <AppButton variant="ghost" onClick={() => void nipper.clearPairedPorts()} disabled={upgrading}>
                Clear paired serial port
              </AppButton>
            </Row>
          )}
          {nipper.error && <Notice $tone="error">{nipper.error}</Notice>}
        </ConnectBand>

        <UnitStage aria-labelledby="ecosalt-unit-title">
          <div>
            <CardTitle id="ecosalt-unit-title">{reading ? "Cell on this Nipper" : "No Nipper yet"}</CardTitle>
            {reading ? <HeroSize>{reading.variant.cellSize}g</HeroSize> : null}
            {reading?.serial ? (
              <SerialLine>
                <FieldLabel>Serial number: </FieldLabel>
                <span>{reading.serial}</span>
              </SerialLine>
            ) : (
              <SerialLine>Connect a Nipper. The cell size and serial number show up here.</SerialLine>
            )}
          </div>
          {reading && (
            <>
              <FactLine>
                <FieldLabel>Brand: </FieldLabel>
                {reading.variant.brand}
                {" · "}
                <FieldLabel>Region: </FieldLabel>
                {reading.variant.region}
                {" · "}
                <FieldLabel>Salt: </FieldLabel>
                {reading.variant.lowSalt ? "Low salt" : "Normal salt"}
                {" · "}
                <FieldLabel>Firmware: </FieldLabel>
                {value(reading.firmwareVersion)}
              </FactLine>
              <FactLine>
                <FieldLabel>Original: </FieldLabel>
                {originalSize}
                {target ? (
                  <>
                    {" · "}
                    <FieldLabel>Next step: </FieldLabel>
                    {target}g
                  </>
                ) : null}
              </FactLine>
            </>
          )}
          {reading?.serial && (
            <AppButton
              onClick={() => setConfirmOpen(true)}
              disabled={!target || upgrading || status !== "loggedIn"}
              aria-describedby={reason ? "ecosalt-upgrade-reason" : undefined}
            >
              {target ? `Upgrade to ${target}g` : "Upgrade"}
            </AppButton>
          )}
          {!reading?.serial && reading && (
            <Notice $tone="error">The serial number could not be read, so this unit cannot be recorded or upgraded.</Notice>
          )}
          {reason && (
            <Notice $tone="info" id="ecosalt-upgrade-reason">
              {reason}
            </Notice>
          )}
          {upgradeMessage && (
            <Notice $tone={upgradeMessage.tone} role={upgradeMessage.tone === "error" ? "alert" : "status"}>
              {upgradeMessage.text}
            </Notice>
          )}
          {recordError && <Notice $tone="error">{recordError}</Notice>}
        </UnitStage>
      </Bench>

      {status !== "unsupported" && (
        <RuntimeBand aria-labelledby="ecosalt-runtime-title">
          <RuntimeHead>
            <CardTitle id="ecosalt-runtime-title">Cell runtime</CardTitle>
            <AppButton
              variant="ghost"
              disabled={status !== "loggedIn" || upgrading}
              onClick={() =>
                void nipper.refreshRuntime().catch((e) => notify.error(e instanceof Error ? e.message : "Could not read runtime"))
              }
            >
              Get runtime
            </AppButton>
          </RuntimeHead>
          <RuntimeFigure>
            <dt>Cell runtime</dt>
            <dd>{reading ? value(reading.runtime.cellRuntime) : "—"}</dd>
          </RuntimeFigure>
          <RuntimeFigure>
            <dt>Low salt time</dt>
            <dd>{reading ? value(reading.runtime.lowSaltRuntime) : "—"}</dd>
          </RuntimeFigure>
          <RuntimeFigure>
            <dt>Current cell</dt>
            <dd>{reading ? value(reading.runtime.cell2Runtime) : "—"}</dd>
          </RuntimeFigure>
          <RuntimeFigure>
            <dt>Format</dt>
            <dd>
              {reading?.runtime.format === "COUNTER" ? "Counter" : reading?.runtime.format === "TOTAL" ? "Total" : "—"}
            </dd>
          </RuntimeFigure>
        </RuntimeBand>
      )}

      <EcoSaltAdminVariant
        reading={reading}
        loggedIn={status === "loggedIn"}
        disabled={upgrading}
        onApply={(change) => nipper.writeVariant(change)}
      />

      {status !== "unsupported" && <EcoSaltSerialLog lines={nipper.log} />}
          </>
        }
      />

      <ConfirmDialog
        visible={confirmOpen}
        title="Upgrade cell size"
        message={
          reading && target
            ? `Upgrade Nipper ${reading.serial} from ${reading.variant.cellSize}g to ${target}g? Keep the cable connected until it finishes. This can only be done once per unit.`
            : ""
        }
        onHide={() => !upgrading && setConfirmOpen(false)}
        onConfirm={() => void runUpgrade()}
        isProcessing={upgrading}
        confirmButtonLabel="Upgrade"
        cancelButtonLabel="Cancel"
      />
    </PageContainer>
  );
};

export default EcoSaltUpgrade;
